import React, { FC } from 'react'

// MUI
import { Alert, Box, FormHelperText, Grid, TextField, useMediaQuery, useTheme } from '@mui/material'

// FOrmik + Yup
import { useFormik } from 'formik'
import * as Yup from 'yup'

// axios
import axios from 'axios'

// Notistack
import { useSnackbar } from 'notistack'

// UI
import { CommonButton } from '@/ui'
import { DiscussProjectH2 } from '../styles'

const ContactFormSection: FC = () => {
  const theme = useTheme()
  const isXS = useMediaQuery(theme.breakpoints.only('xs'))

  const { enqueueSnackbar } = useSnackbar()

  const formik = useFormik({
    initialValues: {
      name: '',
      email: '',
      contactNo: '',
      subject: '',
      message: '',
      submit: null,
      success: null,
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Name is required'),
      email: Yup.string().email('Enter Valid Email').required('Email is required'),
      contactNo: Yup.string().required('Contact Number is required'),
      subject: Yup.string().required('Subject is required'),
      message: Yup.string().required('Message is required'),
    }),
    onSubmit: async (values, helpers) => {
      try {
        helpers.setSubmitting(true)

        const payload = {
          name: values.name,
          email: values.email,
          contactNo: values.contactNo,
          subject: values.subject,
          messageBody: values.message,
        }

        const response = await axios({
          url: '/api/contact',
          method: 'post',
          data: payload,
          validateStatus(status) {
            return status >= 200 && status < 599
          },
        })

        switch (response.status) {
          case 200:
            helpers.setErrors({ success: "Thank You for contacting us. We'll get back to you soon." })
            helpers.resetForm()
            enqueueSnackbar("Thank You for contacting us. We'll get back to you soon.", {
              variant: 'success',
              autoHideDuration: 5000,
            })
            break
          case 400:
            helpers.setErrors({ submit: response.data.payload })
            break
          case 404:
            helpers.setErrors({ submit: response.data.payload })
            break
          default:
            helpers.setErrors({ submit: response.data.payload || 'Something Went Wrong' })
            break
        }

        helpers.setSubmitting(false)
      } catch (error) {
        helpers.setSubmitting(false)
        enqueueSnackbar('Something Went Wrong!!', {
          variant: 'error',
          autoHideDuration: 5000,
        })
      }
    },
  })

  return (
    <Box>
      <DiscussProjectH2>Would you like to discuss a project?</DiscussProjectH2>

      <Box sx={{ mt: 5 }} component="form" onSubmit={formik.handleSubmit}>
        <Grid container justifyContent="space-between" spacing={4}>
          <Grid item xs={12} sm={6} md={6}>
            <TextField
              error={Boolean(formik.touched.name && formik.errors.name)}
              fullWidth
              helperText={formik.touched.name && formik.errors.name}
              name="name"
              label="Name"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.name}
              required
              type="text"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <TextField
              error={Boolean(formik.touched.email && formik.errors.email)}
              fullWidth
              helperText={formik.touched.email && formik.errors.email}
              name="email"
              label="Email"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.email}
              required
              type="email"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <TextField
              error={Boolean(formik.touched.contactNo && formik.errors.contactNo)}
              fullWidth
              helperText={formik.touched.contactNo && formik.errors.contactNo}
              name="contactNo"
              label="Phone"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.contactNo}
              required
              type="tel"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <TextField
              error={Boolean(formik.touched.subject && formik.errors.subject)}
              fullWidth
              helperText={formik.touched.subject && formik.errors.subject}
              name="subject"
              label="Subject"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.subject}
              required
              type="text"
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <TextField
              error={Boolean(formik.touched.message && formik.errors.message)}
              fullWidth
              helperText={formik.touched.message && formik.errors.message}
              name="message"
              label="Message"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.message}
              required
              type="text"
              rows={6}
              multiline
              inputProps={{
                style: {
                  resize: 'vertical', // Allow vertical resizing
                },
              }}
            />
          </Grid>
        </Grid>

        {formik.errors.success ? (
          <Box sx={{ my: 3 }}>
            <Alert severity="success" sx={{ fontSize: theme.typography.pxToRem(18) }}>
              {formik.errors.success}
            </Alert>
          </Box>
        ) : null}
        {formik.errors.submit ? (
          <Box sx={{ my: 3 }}>
            <Alert severity="error" sx={{ fontSize: theme.typography.pxToRem(18) }}>
              <FormHelperText error>{formik.errors.submit}</FormHelperText>
            </Alert>
          </Box>
        ) : null}

        <CommonButton type={formik.isSubmitting ? 'button' : 'submit'} fullWidth={isXS} disabled={formik.isSubmitting} sx={{ mt: 3 }}>
          {formik.isSubmitting ? 'Sending....' : 'Send Message'}
        </CommonButton>
      </Box>
    </Box>
  )
}

export default ContactFormSection
